import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

/**
 * Lands here as the OAuth redirect_uri configured for the Hauraki Web IDE
 * client. react-oidc-context's AuthProvider auto-processes the `code`+`state`
 * params on the URL via UserManager.signinRedirectCallback() — this page
 * just shows a spinner while that happens, then routes the user on.
 *
 * Success -> /dashboard.  Error -> /login?error=...  Still-loading -> wait.
 */
const AuthCallback: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isLoading) return;

    if (auth.error) {
      const msg = encodeURIComponent(auth.error.message || 'Sign-in failed');
      navigate(`/login?error=${msg}`, { replace: true });
      return;
    }

    if (auth.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Completing sign-in"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        background: '#1e1e1e',
        color: '#e0e0e0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: '2px solid #3a3a3a',
          borderTopColor: '#888',
          borderRadius: '50%',
          animation: 'auth-callback-spin 0.9s linear infinite',
        }}
      />
      <span>Completing sign-in…</span>
      <style>{`@keyframes auth-callback-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthCallback;
